/* eslint-disable */
// Factures list component
import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, Button, Typography, Skeleton, CircularProgress } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import iconEye from 'Images/Icon_eye.svg';
import fileIcon from 'Images/file_icon.svg';
import searchIcon from '../../Images/search_icon.svg';
import AppButton from 'Shared/UI/Button';
import { CustomTable, TablePagination } from 'Shared/Table';
import StatusBadge from 'Shared/UI/StatusBadge';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import useNetworkLoader from 'hooks/useNetworkLoader';
import useUser from 'hooks/useUser';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { formatAmount } from 'Utils/ulilities';
import ExportFacturesModal from './ExportFacturesModal';
import DateRangePicker from 'Shared/UI/DateRangePicker';
import DropdownSelect from 'Shared/UI/DropdownSelect';
import { getFactures, getFactureTotals, downloadFacturePdf, exporterFactures } from './FacturesServices';
import toast from 'Utils/toast';

const STATUT_OPTIONS = [
  { value: 'reglee', label: 'Réglée' },
  { value: 'non-reglee', label: 'Non réglée' },
  { value: 'non-reglee-echue', label: 'Non réglée échue' },
  { value: 'partiellement-reglee', label: 'Partiellement réglée' },
  { value: 'impayee', label: 'Impayée' },
];

const STATUT_OPTIONS_MF = [
  { value: 'reglee', label: 'Validé' },
  { value: 'non-reglee', label: 'Non validé' },
  { value: 'non-reglee-echue', label: 'Expiré' },
  { value: 'partiellement-reglee', label: 'Partiel' },
  { value: 'impayee', label: 'Rejeté' },
];

const fmt = (v) => formatAmount(v || 0);

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d)) return '—';
  return format(d, 'dd/MM/yyyy');
};

const formatDateLabel = (dateStr) => {
  if (!dateStr) return 'Sans échéance';
  const d = new Date(dateStr);
  if (isNaN(d)) return 'Sans échéance';
  return format(d, 'dd MMMM yyyy', { locale: fr });
};

const parseUrlParams = (search) => {
  const p = new URLSearchParams(search);
  const toDate = (str) => { if (!str) return null; const d = new Date(str); return isNaN(d) ? null : d; };
  return {
    search: p.get('q') || '',
    selectedStatuts: p.getAll('statut'),
    dateRange: { start: toDate(p.get('dateStart')), end: toDate(p.get('dateEnd')) },
    dateRangeEcheance: { start: toDate(p.get('echeanceStart')), end: toDate(p.get('echeanceEnd')) },
    page: parseInt(p.get('page') || '1', 10),
    pageSize: parseInt(p.get('size') || '10', 10),
  };
};

const groupByEcheance = (rows) => {
  const map = {};
  rows.forEach((row) => {
    const key = row.dateEcheance || 'none';
    if (!map[key]) map[key] = [];
    map[key].push(row);
  });
  return Object.entries(map).map(([key, items]) => ({
    id: key,
    date: formatDateLabel(key === 'none' ? null : key),
    count: items.length,
    total: items.reduce((s, r) => s + (r.amount || 0), 0),
    rows: items,
  }));
};

const Facture = () => {
  const history = useHistory();
  const location = useLocation();
  const { callApi } = useNetworkLoader();
  const isMF = useUser();

  const initial = parseUrlParams(location.search);
  const [search, setSearch] = useState(initial.search);
  const [debouncedSearch, setDebouncedSearch] = useState(initial.search);
  const debounceRef = useRef(null);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setDebouncedSearch(val); setPage(1); }, 350);
  };
  const [selectedStatuts, setSelectedStatuts] = useState(initial.selectedStatuts);
  const [dateRange, setDateRange] = useState(initial.dateRange);
  const [dateRangeEcheance, setDateRangeEcheance] = useState(initial.dateRangeEcheance);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [allExpanded, setAllExpanded] = useState(false);
  const [page, setPage] = useState(initial.page);
  const [pageSize, setPageSize] = useState(initial.pageSize);
  const [exportOpen, setExportOpen] = useState(false);
  const [localSort, setLocalSort] = useState({ field: null, dir: 'asc' });
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadingReleve, setDownloadingReleve] = useState(false);

  const [factures, setFactures] = useState(null);
  const [totalFactures, setTotalFactures] = useState(0);
  const loading = factures === null;

  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const reqIdRef = useRef(0);

  useEffect(() => {
    setFactures(null);
    const reqId = ++reqIdRef.current;
    const params = {
      page: page - 1,
      size: pageSize,
      sort: 'date,desc',
      statuses: selectedStatuts,
      startDate: dateRange.start ? format(dateRange.start, 'yyyy-MM-dd') : '',
      endDate: dateRange.end ? format(dateRange.end, 'yyyy-MM-dd') : '',
      startEcheance: dateRangeEcheance.start ? format(dateRangeEcheance.start, 'yyyy-MM-dd') : '',
      endEcheance: dateRangeEcheance.end ? format(dateRangeEcheance.end, 'yyyy-MM-dd') : '',
    };
    callApi(
      () => getFactures(params, debouncedSearch),
      (data) => {
        if (reqId !== reqIdRef.current) return;
        setFactures(data?.content || []);
        setTotalFactures(data?.totalElements || 0);
      },
    );
  }, [page, pageSize, debouncedSearch, selectedStatuts, dateRange.start, dateRange.end, dateRangeEcheance.start, dateRangeEcheance.end, refreshTrigger]);

  useEffect(() => {
    const p = new URLSearchParams();
    if (debouncedSearch) p.set('q', debouncedSearch);
    selectedStatuts.forEach((s) => p.append('statut', s));
    if (dateRange.start) p.set('dateStart', format(dateRange.start, 'yyyy-MM-dd'));
    if (dateRange.end) p.set('dateEnd', format(dateRange.end, 'yyyy-MM-dd'));
    if (dateRangeEcheance.start) p.set('echeanceStart', format(dateRangeEcheance.start, 'yyyy-MM-dd'));
    if (dateRangeEcheance.end) p.set('echeanceEnd', format(dateRangeEcheance.end, 'yyyy-MM-dd'));
    if (page !== 1) p.set('page', page);
    if (pageSize !== 10) p.set('size', pageSize);
    history.replace({ search: p.toString() });
  }, [debouncedSearch, selectedStatuts, dateRange.start, dateRange.end, dateRangeEcheance.start, dateRangeEcheance.end, page, pageSize]);

  const groups = groupByEcheance(factures || []);

  const handleLocalSort = (field) => {
    setLocalSort(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortRows = (rows) => {
    if (!localSort.field) return rows;
    return [...rows].sort((a, b) => {
      let va = a[localSort.field] ?? '';
      let vb = b[localSort.field] ?? '';
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return localSort.dir === 'asc' ? -1 : 1;
      if (va > vb) return localSort.dir === 'asc' ? 1 : -1;
      return 0;
    });
  };
const handleToggleGroup = (id) => {
  setExpandedGroups((prev) => ({ ...prev, [id]: prev[id] !== false ? false : true }));
};

const handleToggleAll = (expand) => {
  if (expand) {
    setExpandedGroups({});
  } else {
    const all = {};
    groups.forEach((g) => { all[g.id] = false; });
    setExpandedGroups(all);
  }
  setAllExpanded(expand);
};

const allColumns = [
  {
    id: 'reference',
    label: isMF ? 'N° Devis' : 'N° Facture',
    width: '12%',
    mfWidth: '22%',
    sortable: true,
    render: (row) => (
      <Typography
        onClick={() => history.push(`/factures/${row.id}`, { facture: row })}
        sx={{
          fontSize: '14px', fontWeight: 600, color: '#000000',
          cursor: 'pointer',
          '&:hover': { color: '#2557A7', textDecoration: 'underline' },
        }}
      >
        {row.reference}
      </Typography>
    ),
  },
  {
    id: 'date',
    label: isMF ? "Date du devis" : "Date d'émission",
    width: '10%',
    mfWidth: '18%',
    render: (row) => (
      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#000000' }}>
        {formatDate(row.date)}
      </Typography>
    ),
  },
  {
    id: 'statut',
    label: 'Statut',
    width: '12%',
    mfWidth: '20%',
    sortable: true,
    render: (row) => <StatusBadge status={row.statut} isMF={isMF} />,
  },
  {
    id: 'amount',
    label: 'Montant',
    mfWidth: '32%',
    sortable: true,
    render: (row) => {
      const hasRestant = row.montantRestant > 0;
      const restantColor = row.statut === 'NON_REGLE_ECHUE' ? '#EF456E' : '#FEA26F';
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', overflow: 'hidden' }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#000000', lineHeight: 1.2, mb: '2px' }}>
            {fmt(row.amount)} Dh
          </Typography>
          {hasRestant && !isMF && (
            <Typography sx={{ fontSize: '12px', color: '#707070', fontWeight: 600, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
              Réglé: {fmt(row.montantRegle)} | <span style={{ color: restantColor }}>Restant: {fmt(row.montantRestant)}</span>
            </Typography>
          )}
        </Box>
      );
    },
  },
  {
    id: 'documentsCount',
    label: 'Docs. attachés',
    mfHidden: true,
    width: '11%',
    render: (row) => {
      const count = row.reglements?.length ?? 0;
      return <Typography sx={{ fontSize: '14px', fontWeight: 600, color: count > 0 ? '#000000' : '#8397AE' }}>{count > 0 ? count : '—'}</Typography>;
    },
  },
  {
    id: 'dateEcheance',
    label: 'Échéance règl.',
    mfHidden: true,
    width: '13%',
    render: (row) => {
      const reg = row.reglements?.[0];
      return (
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: reg ? '#000000' : '#8397AE' }}>
          {reg ? formatDate(reg.rgDateEcheance) : '—'}
        </Typography>
      );
    },
  },
  {
    id: 'reglementStatut',
    label: 'Statut règl.',
    mfHidden: true,
    width: '13%',
    render: (row) => {
      const regs = row.reglements || [];
      if (regs.length === 0) return <Typography sx={{ fontSize: '14px', color: '#8397AE' }}>—</Typography>;
      const hasImpaye = regs.some((r) => r.impaye);
      const cfg = hasImpaye
        ? { label: 'Impayé', color: '#EF456E', bg: '#EF456E05', border: '#EF456E' }
        : { label: 'Payé', color: '#12B981', bg: '#ECFEF6', border: '#12B981' };
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', px: '10px', py: '4px', borderRadius: '20px', border: `1px solid ${cfg.border}`, backgroundColor: cfg.bg, whiteSpace: 'nowrap' }}>
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: cfg.color }} />
          <Typography sx={{ fontSize: '11px', fontWeight: 600, color: cfg.color }}>{cfg.label}</Typography>
        </Box>
      );
    },
  },
  {
    id: 'action',
    label: 'Action',
    width: '8%',
    mfWidth: '8%',
    render: (row) => (
      <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
        <img
          src={iconEye}
          alt="view"
          onClick={() => history.push(`/factures/${row.id}`, { facture: row })}
          style={{ width: 20, height: 24, objectFit: 'contain', cursor: 'pointer', opacity: 1, color: '#95A1B8' }}
        />
        {downloadingId === row.id
          ? <CircularProgress size={18} sx={{ color: '#007BC6' }} />
          : <FileDownloadOutlinedIcon
              onClick={() => handleDownload(row)}
              sx={{ fontSize: 20, color: '#A0AEC0', cursor: 'pointer', '&:hover': { color: '#2557A7' } }}
            />
        }
      </Box>
    ),
  },
];

const columns = isMF
  ? allColumns.filter((c) => !c.mfHidden).map((c) => c.mfWidth ? { ...c, width: c.mfWidth } : c)
  : allColumns;

const handleDownloadReleve = async () => {
  if (downloadingReleve) return;
  setDownloadingReleve(true);
  try {
    const response = await exporterFactures(isMF ? { officiel: false, type: 'all' } : { officiel: true });
    const disposition = response?.headers?.['content-disposition'] || '';
    const filename = disposition.split('filename=')[1] || `releve-${new Date().toISOString().slice(0, 10)}.pdf`;
    const url = window.URL.createObjectURL(new Blob([response?.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    if (!e?.handled) {
      toast.error(e?.response?.data?.statusLabel || 'Erreur lors de la génération du relevé');
    }
  } finally { setDownloadingReleve(false); }
};

const handleDownload = (row) => {
  if (!row.reference || downloadingId === row.id) return;
  setDownloadingId(row.id);
  callApi(
    () => downloadFacturePdf(row.reference),
    (blob) => {
      const url = window.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${row.reference}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      setDownloadingId(null);
    },
  );
};
  return (
    <Box sx={{ backgroundColor: '#F8FAFC', pt: '90px', px: { xs: '16px', md: '40px' }, pb: '40px', mt: '-80px', fontFamily: "'Open Sans', sans-serif", '& *': { fontFamily: "'Open Sans', sans-serif" } }}>
    <ExportFacturesModal open={exportOpen} onClose={() => setExportOpen(false)} isMF={isMF} />

      {/** Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '16px', mt: '8px' }}>
        {/** Left: title + subtitle */}
        <Box>
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#000000' }}>
            {isMF ? 'Mes devis' : 'Mes factures'}
            <Box component="span" sx={{ fontSize: '20px', fontWeight: 700, color: '#000000', ml: '4px' }}>
              {loading ? <>(<Skeleton component="span" variant="text" width={24} sx={{ display: 'inline-block', verticalAlign: 'middle' }} />)</> : `(${totalFactures})`}
            </Box>
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#385576', fontWeight: 600 }}>
            {isMF ? 'Consultez, filtrez et téléchargez l\'historique de vos devis' : 'Consultez, filtrez et téléchargez l\'historique de vos factures'}
          </Typography>
        </Box>
        {/** Right: action buttons */}
        <Box sx={{ display: 'flex', gap: '12px' }}>
          <AppButton
            variant="custom"
            label={isMF ? 'Exporter les devis' : 'Exporter les factures'}
            icon={<img src={fileIcon} alt="file" style={{ width: 18, height: 18 }} />}
            color="#FFFFFF"
            textColor="#385576"
            hoverColor="#F1F5F9"
            onClick={() => setExportOpen(true)}
            sx={{ border: 'none', borderRadius:'8px', fontWeight: 600, fontSize: '13px', height: '40px', width:'198px', mr:'-4px', p:'4px', boxShadow: '0px 2px 8px rgba(0,0,0,0.10)', '&:hover': { boxShadow: '0px 4px 12px rgba(0,0,0,0.14)', backgroundColor: '#F1F5F9' } }}
          />
          <AppButton
            variant="custom"
            label="Télécharger mon relevé"
            icon={<FileDownloadOutlinedIcon />}
            color="#007BC6"
            hoverColor="#005F9E"
            textColor="#FFFFFF"
            onClick={handleDownloadReleve}
            loading={downloadingReleve}
            sx={{ fontWeight: 600, fontSize: '13px', pl: '16px', pr: '16px', height: '40px', width: '216px', '& .MuiButton-startIcon': { mr: '2px' } }}
          />
        </Box>
      </Box>

      {/** Filter Bar */}
      <Box sx={{ position: 'sticky', top: '62px', zIndex: 10, backgroundColor: '#F8FAFC', pt: '8px', pb: '0px' }}>
      <Box sx={{ backgroundColor: '#fff', border: '1px solid #DEE5EE', borderRadius: '10px', p: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        {/** Search */}
        <Box sx={{ flex: 1, minWidth: '200px', height: '45px', display: 'flex', alignItems: 'center', border: '1px solid #DEE5EE', borderRadius: '8px', px: '12px', gap: '8px', backgroundColor: '#F8FAFC' }}>
          <img src={searchIcon} alt="search" style={{ width: 20, height: 20, flexShrink: 0, objectFit: 'contain' }} />
          <input
            placeholder={isMF ? 'Rechercher par N° devis ou montant...' : 'Rechercher par N° facture ou montant...'}
            value={search}
            onChange={handleSearchChange}
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: '12px', color: '#000000', width: '100%', fontFamily: "'Open Sans', sans-serif" }}
          />
        </Box>

        {/** Statut dropdown */}
        <Box sx={{ flex: 1, minWidth: '120px' }}>
          <DropdownSelect
            options={isMF ? STATUT_OPTIONS_MF : STATUT_OPTIONS}
            value={selectedStatuts}
            onChange={(v) => { setSelectedStatuts(v); setPage(1); }}
            placeholder="Statut"
            multiple
            width="100%"
          />
        </Box>

        {/** Date d'émission */}
        <Box sx={{ flex: 1, minWidth: '140px' }}>
          <DateRangePicker
            placeholder={isMF ? "Date du devis" : "Date d'émission"}
            value={dateRange}
            onApply={(start, end) => { setDateRange({ start, end }); setPage(1); }}
            width="100%"
          />
        </Box>

        {/** Date d'échéance — hidden for MF */}
        {!isMF && (
          <Box sx={{ flex: 1, minWidth: '140px' }}>
            <DateRangePicker
              placeholder="Date d'échéance"
              value={dateRangeEcheance}
              onApply={(start, end) => { setDateRangeEcheance({ start, end }); setPage(1); }}
              width="100%"
            />
          </Box>
        )}

        {/** Réinitialiser */}
        <Button onClick={() => { setSearch(''); setDebouncedSearch(''); setSelectedStatuts([]); setDateRange({ start: null, end: null }); setDateRangeEcheance({ start: null, end: null }); setPage(1); }} variant="contained"
          sx={{ height: '45px', width: '159px', borderRadius: '8px', backgroundColor: '#007BC6', textTransform: 'none', fontWeight: 600, fontSize: '13px', flexShrink: 0, '&:hover': { backgroundColor: '#005F9E' } }}>
          Réinitialiser
        </Button>
      </Box>
      </Box>

      {/** Tout développer / Tout réduire */}
      <Box sx={{ display: 'flex', gap: '16px', mb: '12px', mt: '8px', py: '6px', position: 'sticky', top: '127px', zIndex: 9, backgroundColor: '#F8FAFC', boxShadow: '0 16px 0 0 #F8FAFC' }}>
        <Box
          onClick={() => handleToggleAll(true)}
          sx={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          <UnfoldMoreIcon sx={{ fontSize: 16, color: '#027BC6' }} />
          <Typography sx={{ fontSize: '13px', color: '#027BC6', fontWeight: 600 }}>Tout développer</Typography>
        </Box>
        <Box
          onClick={() => handleToggleAll(false)}
          sx={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          <UnfoldLessIcon sx={{ fontSize: 16, color: '#027BC6' }} />
          <Typography sx={{ fontSize: '13px', color: '#027BC6', fontWeight: 600 }}>Tout réduire</Typography>
        </Box>
      </Box>
      {/**Groups list */}
      <Box sx={{ backgroundColor: '#fff', border: '1px solid #D1D5DB', borderRadius: 0, overflow: 'visible' }}>
        {loading ? (
          [0, 1, 2, 3].map((i) => (
            <Box key={i} sx={{ backgroundColor: i % 2 === 0 ? '#FFFFFF' : '#F8FAFC', borderTop: i > 0 ? '1px solid #E2E8F0' : 'none', borderLeft: '3px solid transparent' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '20px', py: '14px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <KeyboardArrowRightIcon sx={{ fontSize: 20, color: '#D1D5DB' }} />
                  <Skeleton variant="text" width={100} height={24} />
                  <Typography sx={{ fontSize: '14px', color: '#D1D5DB' }}> - </Typography>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#D1D5DB' }}>{"Date d'échéance :"}</Typography>
                  <Skeleton variant="text" width={140} height={24} />
                </Box>
                <Skeleton variant="text" width={110} height={24} />
              </Box>
            </Box>
          ))
        ) : groups.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: '60px', color: '#8F8F8F', fontSize: '14px' }}>
            {isMF ? 'Aucun devis trouvé' : 'Aucune facture trouvée'}
          </Box>
        ) : groups.map((group, gi) => (
          <Box key={group.id} sx={{ backgroundColor: gi % 2 === 0 ? '#FFFFFF' : '#F8FAFC', borderTop: gi > 0 ? '1px solid #E2E8F0' : 'none', borderLeft: expandedGroups[group.id] !== false ? '3px solid #007BC6' : '3px solid transparent' }}>
            <Box sx={{ position: 'sticky', top: '159px', zIndex: 8, backgroundColor: gi % 2 === 0 ? '#FFFFFF' : '#F8FAFC', boxShadow: `0 14px 0 0 ${gi % 2 === 0 ? '#FFFFFF' : '#F8FAFC'}` }}>
            <Box onClick={() => handleToggleGroup(group.id)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '20px', py: '14px', cursor: 'pointer', '&:hover': { backgroundColor: '#F8FAFC' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {expandedGroups[group.id] !== false
                  ? <KeyboardArrowDownIcon sx={{ fontSize: 20, color: '#007BC6' }} />
                  : <KeyboardArrowRightIcon sx={{ fontSize: 20, color: '#007BC6' }} />}
                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#385576' }}>{group.count} {isMF ? 'Devis' : `Facture${group.count > 1 ? 's' : ''}`}</Typography>
                <Typography sx={{ fontSize: '14px', color: '#385576' }}> - </Typography>
                {!isMF && <Typography sx={{ fontSize: '14px', fontWeight: 600, lineHeight: '32px', color: '#8397AE', fontFamily: "'Open Sans', sans-serif", letterSpacing: 0 }}>{"Date d'échéance :"}</Typography>}
                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#385576', ml: '-6px' }}>{group.date}</Typography>
              </Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#8397AE' }}>Total : <Box component="span" sx={{ color: "#385576" }}>{fmt(group.total)} Dhs</Box></Typography>
            </Box>
            </Box>
            {expandedGroups[group.id] !== false && (
              <>
                <Box sx={{ borderTop: '1px solid #E2E8F0' }} />
                <Box sx={{ mx: '16px', pb: '16px', mt: '8px' }}>
                  <CustomTable
                    columns={columns}
                    data={sortRows(group.rows)}
                    sortField={localSort.field}
                    sortDir={localSort.dir}
                    onSort={handleLocalSort}
                    emptyMessage={isMF ? 'Aucun devis' : 'Aucune facture'}
                    stickyTop="222px"
                    stickyBg={gi % 2 === 0 ? '#FFFFFF' : '#F8FAFC'}
                    sx={{
                      border: 'none', borderRadius: 0,
                      '& thead': { backgroundColor: gi % 2 === 0 ? '#FFFFFF' : '#F8FAFC' },
                      '& thead th': { borderBottom: 'none', color: '#385576', fontSize: '14px' },
                      '& table': { tableLayout: 'fixed', width: '100%', borderCollapse: 'separate', borderSpacing: '0' },
                      '& tbody tr': { backgroundColor: gi % 2 === 0 ? '#F8FAFC' : '#FFFFFF', height: '55px', maxHeight: '55px' },
                      '& tbody tr td': { borderBottom: '1px solid #D1D5DB', borderTop: '1px solid #D1D5DB' },
                      '& tbody tr td:first-of-type': { borderLeft: '1px solid #D1D5DB' },
                      '& tbody tr td:last-of-type': { borderRight: '1px solid #D1D5DB' },
                      '& tbody tr:not(:last-of-type) td': { borderBottom: '1px solid #D1D5DB' },
                      '& tbody tr:not(:first-of-type) td': { borderTop: 'none' },
                      '& tbody tr:first-of-type td:first-of-type': { borderRadius: '8px 0 0 0' },
                      '& tbody tr:first-of-type td:last-of-type': { borderRadius: '0 8px 0 0' },
                      '& tbody tr:last-of-type td:first-of-type': { borderRadius: '0 0 0 8px' },
                      '& tbody tr:last-of-type td:last-of-type': { borderRadius: '0 0 8px 0' },
                    }}
                  />
                </Box>
              </>
            )}
          </Box>
        ))}
        <Box sx={{ borderTop: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', backgroundColor: '#FFFFFF' }}>
          <TablePagination
            page={page}
            pageSize={pageSize}
            total={totalFactures}
            onChange={(newPage) => setPage(newPage)}
            pageSizeOptions={[10, 20, 50]}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
            sx={{ width: '100%' }}
          />
        </Box>
      </Box>


    </Box>
  );
};

export default Facture;